import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { SEO } from '../components/SEO';
import { Heart, Leaf, Sparkles, Apple, Carrot, Salad, Search } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultBenefits = [
  {
    icon: Heart,
    title: { fi: 'Sydänterveys', fr: 'Santé Cardiaque', en: 'Heart Health' },
    content: {
      fi: 'Vegaaninen ruokavalio voi alentaa verenpainetta ja kolesterolia, vähentäen sydänsairauksien riskiä.',
      fr: 'Un régime végétalien peut réduire la tension artérielle et le cholestérol, diminuant le risque de maladies cardiaques.',
      en: 'A vegan diet can lower blood pressure and cholesterol, reducing the risk of heart disease.'
    }
  },
  {
    icon: Sparkles,
    title: { fi: 'Paino ja Energia', fr: 'Poids et Énergie', en: 'Weight & Energy' },
    content: {
      fi: 'Kasvisruokavalio auttaa ylläpitämään terveellistä painoa ja lisää energiatasoja luonnollisesti.',
      fr: "Un régime végétal aide à maintenir un poids santé et augmente naturellement les niveaux d'énergie.",
      en: 'A plant-based diet helps maintain a healthy weight and naturally increases energy levels.'
    }
  },
  {
    icon: Apple,
    title: { fi: 'Ravintoaineet', fr: 'Nutriments', en: 'Nutrients' },
    content: {
      fi: 'Monipuolinen vegaaninen ruokavalio tarjoaa runsaasti kuitua, vitamiineja ja antioksidantteja.',
      fr: 'Un régime végétalien varié offre beaucoup de fibres, de vitamines et d\'antioxydants.',
      en: 'A varied vegan diet provides plenty of fiber, vitamins, and antioxidants.'
    }
  },
  {
    icon: Search,
    title: { fi: 'Mistä löydän ravinteet', fr: 'Où trouver les nutriments', en: 'Where to find nutrients' },
    content: {
      fi: 'Proteiini: palkokasvit, tofu, tempeh, seitan, pähkinät. B12: ravintolisä tai rikastetut tuotteet. Rauta: linssit, pinaatti, tahini. Kalsium: lehtikasvit, manteli, rikastetut kasvimaidot. Omega-3: pellavansiemenet, chia, saksanpähkinät.',
      fr: 'Protéines: légumineuses, tofu, tempeh, seitan, noix. B12: supplément ou produits enrichis. Fer: lentilles, épinards, tahini. Calcium: légumes verts, amandes, laits végétaux enrichis. Oméga-3: graines de lin, chia, noix.',
      en: 'Protein: legumes, tofu, tempeh, seitan, nuts. B12: supplement or fortified products. Iron: lentils, spinach, tahini. Calcium: leafy greens, almonds, fortified plant milks. Omega-3: flaxseeds, chia, walnuts.'
    }
  },
  {
    icon: Carrot,
    title: { fi: 'Ruoansulatus', fr: 'Digestion', en: 'Digestion' },
    content: {
      fi: 'Runsas kuidunsaanti parantaa suoliston terveyttä ja ruoansulatusta.',
      fr: 'Un apport élevé en fibres améliore la santé intestinale et la digestion.',
      en: 'High fiber intake improves gut health and digestion.'
    }
  },
  {
    icon: Salad,
    title: { fi: 'Tulehdukset', fr: 'Inflammation', en: 'Inflammation' },
    content: {
      fi: 'Kasvisruoka vähentää kroonista tulehdusta kehossa.',
      fr: 'Les aliments végétaux réduisent l\'inflammation chronique dans le corps.',
      en: 'Plant foods reduce chronic inflammation in the body.'
    }
  },
  {
    icon: Leaf,
    title: { fi: 'Pitkäikäisyys', fr: 'Longévité', en: 'Longevity' },
    content: {
      fi: 'Tutkimukset osoittavat, että kasvissyöjät elävät keskimäärin pidempään.',
      fr: 'Les études montrent que les végétaliens vivent en moyenne plus longtemps.',
      en: 'Studies show that vegans tend to live longer on average.'
    }
  }
];

const iconMap = { Heart, Sparkles, Apple, Carrot, Salad, Leaf };

export const Health = () => {
  const { t, getLocalizedText, language } = useLanguage();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  const getSEOContent = () => {
    const content = {
      en: {
        title: 'Vegan Health Benefits - Plant-Based Nutrition | love4vegan',
        description: 'Discover the health benefits of a vegan diet. Learn about heart health, nutrition, weight management, and how plant-based eating can transform your life.'
      },
      fi: {
        title: 'Vegaanin Terveyshyödyt - Kasvisruoan Ravitsemus | love4vegan',
        description: 'Tutustu vegaanisen ruokavalion terveyshyötyihin. Opi sydänterveydestä, ravitsemuksesta ja kuinka kasvisruoka voi muuttaa elämäsi.'
      },
      fr: {
        title: 'Bienfaits Santé Végans - Nutrition Végétalienne | love4vegan',
        description: 'Découvrez les bienfaits d\'un régime végétalien. Santé cardiaque, nutrition, gestion du poids et plus encore.'
      }
    };
    return content[language] || content.en;
  };

  const seoContent = getSEOContent();

  useEffect(() => {
    fetchPageContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/pages/health`);
      setPageData(response.data);
    } catch (error) {
      console.error('Error fetching page content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Use DB content or defaults
  const title = pageData?.title || { fi: 'Terveys ja Veganismi', fr: 'Santé et Véganisme', en: 'Health and Veganism' };
  const intro = pageData?.content || { 
    fi: 'Vegaaninen ruokavalio tarjoaa monia terveyshyötyjä, kun se toteutetaan oikein.',
    fr: 'Un régime végétalien offre de nombreux avantages pour la santé lorsqu\'il est bien mené.',
    en: 'A vegan diet offers many health benefits when done correctly.'
  };
  
  // Use sections from DB or default benefits
  const sections = (pageData?.sections && pageData.sections.length > 0) ? pageData.sections : defaultBenefits;
  const icons = [Heart, Sparkles, Apple, Search, Carrot, Salad, Leaf];

  return (
    <div className="min-h-screen py-12" data-testid="health-page">
      <SEO 
        title={seoContent.title}
        description={seoContent.description}
        image="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=1200"
        url="https://love4vegan.com/health"
        type="article"
        keywords="vegan health, plant-based nutrition, vegaani terveys, santé végane"
      />
      {/* Hero */}
      <section className="relative py-20 mb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
              {getLocalizedText(title)}
            </h1>
            <p className="text-xl text-foreground/70 leading-relaxed">
              {getLocalizedText(intro)}
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sections.map((section, index) => {
              const Icon = section.icon || icons[index % icons.length];
              return (
                <div 
                  key={index}
                  className="group p-8 rounded-3xl bg-card border border-border hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                  data-testid={`health-benefit-${index}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">
                    {getLocalizedText(section.title)}
                  </h3>
                  <p className="text-foreground/60 leading-relaxed">
                    {getLocalizedText(section.content)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-16 bg-accent/30">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="font-accent text-2xl text-secondary mb-4">
            {getLocalizedText({
              fi: 'Muista!',
              fr: 'N\'oubliez pas!',
              en: 'Remember!'
            })}
          </div>
          <p className="text-foreground/70 leading-relaxed">
            {getLocalizedText({
              fi: 'Monipuolinen vegaaninen ruokavalio voi tarjota kaikki tarvittavat ravintoaineet. Harkitse B12-vitamiinilisää ja pidä huolta riittävästä proteiinin, raudan ja kalsiumin saannista.',
              fr: 'Un régime végétalien varié peut fournir tous les nutriments nécessaires. Envisagez un supplément de B12 et assurez-vous d\'avoir suffisamment de protéines, de fer et de calcium.',
              en: 'A varied vegan diet can provide all necessary nutrients. Consider a B12 supplement and ensure adequate protein, iron, and calcium intake.'
            })}
          </p>
        </div>
      </section>
    </div>
  );
};
